// ./src/components/features/Cars/DriveTypeSelector/DriveTypeSelector.tsx
import React, { useMemo, useCallback } from "react";
import { Select, SelectProps, Group, Text, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic";
import {
  DRIVE_TYPE_OPTIONS,
  getDriveTypeIcon,
} from "@/config/driveTypes";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";

// ============================================
// Types
// ============================================

export interface DriveTypeSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange" | "name"> {
  /** Field name for form binding */
  name?: string;

  /** Currently selected drive type value */
  value?: string | null | undefined;

  /** Callback fired when drive type changes: (name, value) */
  onChange?: (name: string, value: string | null) => void;

  /** Placeholder text */
  placeholder?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;
}

// ============================================
// Option Data Interface
// ============================================

interface DriveTypeOptionData {
  value: string;
  label: string;
  labelShort: string;
  iconName: string;
}

// ============================================
// Component
// ============================================

const DriveTypeSelectorComponent: React.FC<DriveTypeSelectorProps> = ({
  name = "driveType",
  value,
  onChange,
  placeholder,
  required = false,
  error,
  disabled = false,
  size = "md",
  label,
  ...selectProps
}) => {
  const { t } = useTranslation(["cars", "common"]);

  // Default translations
  const defaultPlaceholder =
    placeholder || t("cars:selectors.driveType.placeholder");
  const defaultLabel = label ?? t("cars:selectors.driveType.label");

  // Build options list with translated labels
  const driveTypeOptions = useMemo((): DriveTypeOptionData[] => {
    return DRIVE_TYPE_OPTIONS.map((option) => ({
      value: option.value,
      label: t(`common:${option.labelKey}`),
      labelShort: t(`common:${option.labelShortKey}`),
      iconName: option.iconName,
    }));
  }, [t]);

  // Get current icon for left section
  const currentIcon = useMemo(() => getDriveTypeIcon(value), [value]);

  // Handle onChange
  const handleChange = useCallback(
    (selectedValue: string | null) => {
      onChange?.(name, selectedValue);
    },
    [name, onChange]
  );

  // Render option for dropdown
  const renderOption: SelectProps["renderOption"] = useCallback(
    ({ option }: any) => {
      const optionData = option as DriveTypeOptionData;
      return (
        <Group gap="sm" wrap="nowrap">
          <AppIcon iconName={optionData.iconName} size={20} />
          <Text size="md" fw={500}>
            {optionData.label}
          </Text>
          <Text size="sm" c="dimmed">
            ({optionData.labelShort})
          </Text>
        </Group>
      );
    },
    []
  );

  // Memoize left section icon
  const leftSection = useMemo(() => {
    return <AppIcon iconName={currentIcon} size={18} />;
  }, [currentIcon]);

  return (
    <Box>
      <Select
        {...selectProps}
        label={defaultLabel}
        size={size}
        value={value ?? null}
        onChange={handleChange}
        placeholder={defaultPlaceholder}
        clearable={!required}
        required={required}
        allowDeselect={!required}
        disabled={disabled}
        error={error}
        data={driveTypeOptions}
        leftSection={leftSection}
        renderOption={renderOption}
        nothingFoundMessage={t("common:form.noOptions")}
        comboboxProps={DRAWER_COMBOBOX_PROPS}
      />
    </Box>
  );
};

export const DriveTypeSelector = React.memo(DriveTypeSelectorComponent);